import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-friendship",
  title: "8 Bible verses about friendship, and what each one costs",
  h1: "Bible verses about friendship",
  description:
    "Eight passages from the World English Bible with a note on each — including the unsentimental ones, which are the useful half and are usually left off the list.",
  targets: [
    "bible verses about friendship",
    "scripture about friends",
    "what does the bible say about friendship",
  ],
  published: "2026-09-03",
  standfirst:
    "Scripture's picture of friendship is less about affection than about proximity: who is close enough to sharpen you, and close enough to pick you up.",
  takeaways: [
    "Ecclesiastes makes the case on entirely practical grounds — two can do more, and one can lift the other when they fall. Nothing about enjoyment.",
    "Proverbs 18:24 warns about having many companions before it praises having one who sticks closer than a brother. The quantity is the risk.",
    "Iron sharpening iron is an abrasive image on purpose. The friendship being described is one that costs something and occasionally hurts.",
    "John 15:15 is the moment the category changes: no longer servants but friends, and the stated basis is that nothing has been withheld.",
  ],
  intro: [
    {
      type: "p",
      text: "Friendship is eighth on the most-searched topic list of the largest topical Bible site, which is higher than most people would guess and lower than the loneliness figures would suggest. The eight below are what Scripture actually says, and roughly half of them are unsentimental in a way that greeting cards are not.",
    },
    {
      type: "p",
      text: "The recurring theme is not affection but proximity and cost. Who is near enough to correct you, near enough to notice, near enough to arrive. That is a harder standard than liking someone, and it is a more useful one to measure a friendship against — particularly if the friendships in question are {mostly conducted through a screen}.",
      links: {
        "mostly conducted through a screen": "/prayers/prayer-for-loneliness",
      },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "Two are better than one, because they have a good reward for their labor. For if they fall, the one will lift up his fellow; but woe to him who is alone when he falls, and doesn't have another to lift him up.",
        ref: "Ecclesiastes 4:9–10",
      },
      note: "The most unsentimental case for friendship in the Bible, made by the book least interested in comforting anybody. The argument is entirely practical: two people accomplish more, and one of them can pick the other up. The woe is not moral disapproval — it is an observation about what happens to a person who falls with nobody there.",
    },
    {
      verse: {
        text: "A man of many companions may be ruined, but there is a friend who sticks closer than a brother.",
        ref: "Proverbs 18:24",
      },
      note: "The warning comes first and it is easy to miss. Having many companions is presented as a hazard rather than an achievement, and the contrast is with a single friend whose closeness exceeds family obligation. In a period where kinship was the strongest bond available, closer than a brother is an extremely strong claim.",
    },
    {
      verse: {
        text: "Iron sharpens iron; so a man sharpens his friend's countenance.",
        ref: "Proverbs 27:17",
      },
      note: "Frequently used as a warm slogan about mutual support, and the image is abrasive on purpose. Sharpening involves friction, removal and noise, and the two pieces of iron are both harder afterwards. The verses either side of it in the chapter are about honest wounds from a friend and about flattery, which tells you what kind of sharpening is meant.",
    },
    {
      verse: {
        text: "No longer do I call you servants, for the servant doesn't know what his lord does. But I have called you friends, for everything that I heard from my Father, I have made known to you.",
        ref: "John 15:15",
      },
      note: "A change of category, stated the night before the arrest, and the basis given is information rather than affection: friends are the ones from whom nothing has been withheld. That is a specific and testable definition, and it is unusual — most definitions of friendship describe a feeling rather than a disclosure.",
    },
    {
      verse: {
        text: "A friend loves at all times; and a brother is born for adversity.",
        ref: "Proverbs 17:17",
      },
      note: "At all times is doing the work, and the second half explains what it guards against: the friend who is present for the good stretch and unreachable during the bad one. Adversity is named as the moment the relationship is for, which reframes a difficult fortnight as the point rather than as an interruption of it.",
    },
    {
      verse: {
        text: "When he had finished speaking to Saul, the soul of Jonathan was knit with the soul of David, and Jonathan loved him as his own soul.",
        ref: "1 Samuel 18:1",
      },
      note: "The Bible's most developed friendship, and it is worth knowing what it cost: Jonathan was the king's son and the heir, and the friendship required him to accept that the throne was going to David. He warns him, protects him and gives him his own robe and weapons. The narrative treats that loss as the measure of the loyalty.",
    },
    {
      verse: {
        text: "In love of the brothers be tenderly affectionate to one another; in honor prefer one another,",
        ref: "Romans 12:10",
      },
      note: "The second clause is the demanding one and it is easy to slide past: prefer one another in honour, which means competing to give it rather than to receive it. Paul is writing to a congregation with real status tensions in it — Jew and Gentile, slave and free, rich and poor — so this is a practical instruction rather than a sentiment.",
    },
    {
      verse: {
        text: "Above all these things, walk in love, which is the bond of perfection.",
        ref: "Colossians 3:14",
      },
      note: "The image is a belt or a binding — the thing that holds a set of garments together, following a list of qualities Paul has just told them to put on. Love here is not another item on the list but what keeps the list from falling off, which makes it a claim about how the other virtues stay attached rather than about how anybody feels.",
    },
    {
      verse: {
        text: "The wounds of a friend are faithful, although the kisses of an enemy are profuse.",
        ref: "Proverbs 27:6",
      },
      note: "Ten verses before the iron-sharpens-iron line and doing the same job more bluntly. The comparison is between something that hurts and is trustworthy and something pleasant that is not, and the word translated faithful is the covenant word — reliable, the sort of thing you can build on. Flattery is treated here as the more dangerous of the two.",
    },
    {
      verse: {
        text: 'So the Scripture was fulfilled which says, "Abraham believed God, and it was accounted to him as righteousness," and he was called the friend of God.',
        ref: "James 2:23",
      },
      note: "The title is unusual enough to be worth stopping on: a man described as God's friend. It is used of Abraham twice more in Scripture, and in every case the context is a long relationship with argument in it — he negotiates over Sodom and is not rebuked for it. Friendship in this sense evidently includes disagreement.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "the-test-these-suggest",
      text: "The test these suggest",
    },
    {
      type: "p",
      text: "Not who you enjoy, but who is close enough to pick you up. Ecclesiastes puts it in exactly those terms, and it is a genuinely useful question to ask about a friendship that has become a group chat.",
    },
    {
      type: "p",
      text: "The practical follow-on is unromantic: ask first, and put a date in the calendar. Almost everybody waiting to be invited is waiting alongside somebody else waiting to be invited, and {the queue never moves} until one of them goes first.",
      links: { "the queue never moves": "/prayers/prayer-for-loneliness" },
    },
    {
      type: "p",
      text: "It is worth saying what none of these passages describe, because the omission is striking. There is no biblical friendship conducted entirely at a distance, no picture of intimacy maintained by messages, and no suggestion that being aware of somebody's news is the same as knowing them. Every relationship in this list involves being in the same place, repeatedly, over years.",
    },
    {
      type: "note",
      text: "Proverbs 27:17 is not a verse about agreeing with each other. If nobody in your life has told you something you did not want to hear this year, the sharpening is not happening.",
    },
  ],
  faq: [
    {
      q: "What does the Bible say about friendship?",
      a: "Less about affection than about proximity and cost — who is close enough to sharpen you, notice you and pick you up. Ecclesiastes 4:9–10 makes the case on entirely practical grounds.",
    },
    {
      q: "What is the best Bible verse about friends?",
      a: "Proverbs 17:17 for constancy, Ecclesiastes 4:9–10 for the unsentimental case, and John 15:15 for the definition — friends are the ones from whom nothing has been withheld.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. None of them prints the divine name, so the Yahweh rendering that distinguishes this edition from most others does not arise on this page.",
    },
  ],
  related: ["social-media-fast-guide", "phone-rules-for-christian-families"],
  relatedTopics: [
    "bible-verses-about-comparison",
    "bible-verses-about-forgiveness",
  ],
};
